import { useState, useEffect, useCallback, useMemo } from "react";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import API from "../lib/api";
import { Activity, ChevronDown, ChevronRight, Cpu, RefreshCw } from "lucide-react";

const PAGE_SIZE = 25;

function formatLatency(ms) {
  if (!Number.isFinite(ms)) return "—";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function summaryTone(status, trimmed) {
  if (status === "error") return "destructive";
  if (trimmed) return "secondary";
  return "outline";
}

export default function AuditLogs() {
  const { auditRun } = useAudit();
  const [tab, setTab] = useState("activity");
  const [logs, setLogs] = useState([]);
  const [activityFilter, setActivityFilter] = useState("");
  const [llmEvents, setLlmEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [llmFilters, setLlmFilters] = useState({ actor: "", status: "" });
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState(null);

  const runId = auditRun?.id || "";

  const fetchLogs = useCallback(async () => {
    setActivityLoading(true);
    try {
      const params = auditRun?.id ? { run_id: auditRun.id } : {};
      const { data } = await API.get("/audit-logs", { params: { ...params, limit: 200 } });
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      setLogs([]);
    } finally {
      setActivityLoading(false);
    }
  }, [auditRun?.id]);

  const fetchLlmStats = useCallback(async () => {
    if (!runId) {
      setStats(null);
      return;
    }
    try {
      const { data } = await API.get(`/audit-runs/${runId}/llm-events/stats`);
      setStats(data || null);
    } catch (e) {
      setStats(null);
    }
  }, [runId]);

  const fetchLlmEvents = useCallback(async () => {
    if (!runId) {
      setLlmEvents([]);
      setTotal(0);
      return;
    }
    setLlmLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
        ...(llmFilters.actor ? { actor: llmFilters.actor } : {}),
        ...(llmFilters.status ? { status: llmFilters.status } : {}),
      };
      const { data } = await API.get(`/audit-runs/${runId}/llm-events`, { params });
      setLlmEvents(data?.events || []);
      setTotal(data?.total || 0);
    } catch (e) {
      setLlmEvents([]);
      setTotal(0);
    } finally {
      setLlmLoading(false);
    }
  }, [llmFilters.actor, llmFilters.status, page, runId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchLlmStats();
  }, [fetchLlmStats]);

  useEffect(() => {
    fetchLlmEvents();
  }, [fetchLlmEvents]);

  useEffect(() => {
    setPage(0);
    setExpandedId(null);
  }, [runId, llmFilters.actor, llmFilters.status]);

  const filteredLogs = useMemo(() => {
    if (!activityFilter) return logs;
    const lowered = activityFilter.toLowerCase();
    return logs.filter((log) => {
      const action = String(log.action || "").toLowerCase();
      const details = JSON.stringify(log.details || {}).toLowerCase();
      return action.includes(lowered) || details.includes(lowered);
    });
  }, [activityFilter, logs]);

  const groupedLogs = useMemo(() => {
    const groups = [];
    const bucket = new Map();
    for (const log of filteredLogs) {
      const groupName = log.audit_run_name || "Unknown Audit";
      if (!bucket.has(groupName)) {
        const value = { name: groupName, logs: [] };
        bucket.set(groupName, value);
        groups.push(value);
      }
      bucket.get(groupName).logs.push(log);
    }
    return groups;
  }, [filteredLogs]);

  const statCards = [
    { label: "LLM calls", value: stats?.stats?.total_calls ?? "—" },
    { label: "Errors", value: stats?.stats?.total_errors ?? "—" },
    { label: "Avg latency", value: stats?.stats?.avg_latency_ms ? formatLatency(Math.round(stats.stats.avg_latency_ms)) : "—" },
    { label: "Total tokens", value: Number.isFinite(stats?.stats?.total_tokens) ? stats.stats.total_tokens.toLocaleString() : "—" },
  ];

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Audit Logs</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {auditRun ? `Logs for: ${auditRun.name}` : "All audit logs"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={tab === "activity" ? "default" : "outline"}
            size="sm"
            onClick={() => setTab("activity")}
            data-testid="activity-tab-btn"
          >
            <Activity className="mr-2 h-4 w-4" />
            Activity
          </Button>
          <Button
            variant={tab === "llm" ? "default" : "outline"}
            size="sm"
            onClick={() => setTab("llm")}
            data-testid="llm-tab-btn"
          >
            <Cpu className="mr-2 h-4 w-4" />
            LLM Events
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              fetchLogs();
              fetchLlmStats();
              fetchLlmEvents();
            }}
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {tab === "llm" && (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            {statCards.map((card) => (
              <Card key={card.label}>
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{card.label}</p>
                  <p className="mt-2 text-2xl font-semibold">{card.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <CardTitle className="text-base">LLM Event Explorer</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {auditRun ? "Structured prompt and response records for the active audit run." : "Select an audit run to inspect LLM events."}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <select
                  value={llmFilters.actor}
                  onChange={(e) => setLlmFilters((prev) => ({ ...prev, actor: e.target.value }))}
                  className="h-9 rounded-md border bg-background px-3 text-sm"
                  data-testid="llm-actor-filter"
                >
                  <option value="">All actors</option>
                  <option value="system">system</option>
                  <option value="agent">agent</option>
                  <option value="user">user</option>
                </select>
                <select
                  value={llmFilters.status}
                  onChange={(e) => setLlmFilters((prev) => ({ ...prev, status: e.target.value }))}
                  className="h-9 rounded-md border bg-background px-3 text-sm"
                  data-testid="llm-status-filter"
                >
                  <option value="">All statuses</option>
                  <option value="success">success</option>
                  <option value="error">error</option>
                </select>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {!runId ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground" data-testid="llm-select-run-empty">
                  Pick an audit run from the sidebar or header, then open this page to inspect its LLM events.
                </div>
              ) : llmLoading && llmEvents.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                  Loading LLM events...
                </div>
              ) : llmEvents.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                  No LLM events found for the current filters.
                </div>
              ) : (
                <div className="space-y-2">
                  {llmEvents.map((event) => {
                    const expanded = expandedId === event.id;
                    return (
                      <div key={event.id} className="overflow-hidden rounded-lg border" data-testid="llm-event-row">
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : event.id)}
                          className="grid w-full grid-cols-1 gap-2 bg-card px-4 py-3 text-left md:grid-cols-[28px_90px_1fr_160px_110px_120px]"
                        >
                          <span className="pt-0.5 text-muted-foreground">
                            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </span>
                          <Badge variant={summaryTone(event.status, event.prompt_trimmed)} className="w-fit font-mono text-[11px]">
                            {event.status === "error" ? "error" : event.prompt_trimmed ? "trimmed" : "ok"}
                          </Badge>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">{event.provider || "unknown"}</span>
                              <Badge variant="outline" className="text-[10px]">{event.audit_run_name || auditRun?.name || "Global"}</Badge>
                              <span className="text-xs text-muted-foreground">{event.actor || "system"}</span>
                              <span className="truncate font-mono text-xs text-muted-foreground">{event.session_id || "—"}</span>
                            </div>
                            <div className="truncate text-xs text-muted-foreground">
                              {event.model_version || "unknown model"}
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">{new Date(event.timestamp).toLocaleString()}</div>
                          <div className="text-sm font-medium">{formatLatency(event.latency_ms)}</div>
                          <div className="text-sm text-muted-foreground">
                            {Number.isFinite(event.total_tokens) ? event.total_tokens.toLocaleString() : "—"} tokens
                          </div>
                        </button>
                        {expanded && (
                          <div className="space-y-4 border-t bg-muted/20 px-4 py-4" data-testid="llm-event-detail">
                            {event.error && (
                              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                {event.error}
                              </div>
                            )}
                            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4 text-sm">
                              <div><span className="text-muted-foreground">Model:</span> {event.model_version || "—"}</div>
                              <div><span className="text-muted-foreground">Temperature:</span> {event.temperature ?? "—"}</div>
                              <div><span className="text-muted-foreground">Prompt tokens:</span> {event.prompt_tokens ?? 0}</div>
                              <div><span className="text-muted-foreground">Completion tokens:</span> {event.completion_tokens ?? 0}</div>
                            </div>
                            {[
                              ["System prompt", event.system_prompt],
                              ["User prompt", event.user_prompt],
                              ["Model output", event.model_output],
                            ].map(([label, value]) => (
                              <div key={label}>
                                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
                                <pre className="max-h-64 overflow-auto rounded-md border bg-background p-3 text-xs whitespace-pre-wrap break-words">
                                  {value || "—"}
                                </pre>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {runId && total > PAGE_SIZE && (
                <div className="flex items-center justify-center gap-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                    data-testid="llm-prev-page"
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * PAGE_SIZE >= total}
                    onClick={() => setPage((current) => current + 1)}
                    data-testid="llm-next-page"
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {tab === "activity" && (
        <Card>
          <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="text-base">Activity Timeline</CardTitle>
              <p className="text-sm text-muted-foreground">
                {auditRun ? "Immutable audit trail for the active run." : "Recent audit activity across accessible runs."}
              </p>
            </div>
            <Input
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value)}
              placeholder="Filter activity..."
              className="h-9 w-full md:w-56"
              data-testid="log-filter-input"
            />
          </CardHeader>
          <CardContent className="p-0">
            {activityLoading && filteredLogs.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <Activity className="mx-auto mb-3 h-10 w-10 opacity-40" />
                <p className="text-sm">Loading activity logs...</p>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <Activity className="mx-auto mb-3 h-10 w-10 opacity-40" />
                <p className="text-sm">No logs found</p>
              </div>
            ) : auditRun ? (
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">Timestamp</TableHead>
                    <TableHead className="w-[180px]">Action</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead className="w-[100px]">Actor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs">
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">{log.action}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[460px] truncate font-mono text-xs">
                        {JSON.stringify(log.details || {}).slice(0, 180)}
                      </TableCell>
                      <TableCell className="text-xs">{log.actor || log.user || "system"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="space-y-6 p-4">
                {groupedLogs.map((group) => (
                  <div key={group.name} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={group.name === "Global" ? "secondary" : "outline"}>{group.name}</Badge>
                      <span className="text-xs text-muted-foreground">{group.logs.length} event{group.logs.length === 1 ? "" : "s"}</span>
                    </div>
                    <Table className="table-dense">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[180px]">Timestamp</TableHead>
                          <TableHead className="w-[180px]">Action</TableHead>
                          <TableHead>Details</TableHead>
                          <TableHead className="w-[100px]">Actor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.logs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell className="font-mono text-xs">
                              {new Date(log.timestamp).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono text-xs">{log.action}</Badge>
                            </TableCell>
                            <TableCell className="max-w-[460px] truncate font-mono text-xs">
                              {JSON.stringify(log.details || {}).slice(0, 180)}
                            </TableCell>
                            <TableCell className="text-xs">{log.actor || log.user || "system"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
